<template>
  <div class="action_mt_box">
    <!-- 1. 映射关系看板 (Source -> Branch [Click/Hold]) -->
    <div class="mt_mapping_board">
      <div class="board_inner">
        <!-- 当前按键 -->
        <div class="key_slot source">
          <div class="slot_label">{{ $t('current_selected_key') }}</div>
          <div class="key_display active" :style="{ fontSize: adjustFontSize(currentKey || '', 110, 22) + 'px' }">{{ currentKey }}</div>
        </div>

        <!-- 分支装饰 -->
        <div class="mapping_branch">
          <div class="branch_line upper"></div>
          <div class="branch_line lower"></div>
          <div class="branch_center">MT</div>
        </div>

        <!-- 目标按键列 -->
        <div class="target_column">
          <!-- Click Key -->
          <trigger_btn class="key_slot target" :label="$t('action_set_click_trigger')" :vks="clickTriggerValue" :tab="tab" :show-edit="true" :large="true" @trigger-click="openKeyDialog(MTTriggerType.click)" />
          <!-- Hold Key -->
          <trigger_btn class="key_slot target" :label="$t('action_set_press_trigger')" :vks="pressTriggerValue" :tab="tab" :show-edit="true" :large="true" @trigger-click="openKeyDialog(MTTriggerType.press)" />
        </div>
      </div>
    </div>

    <!-- 2. 设置区块 -->
    <div class="mt_settings_section">
      <div class="mgx_setting_item_card">
        <div class="setting_header">
          <div class="header_left">
            <svg class="pill_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <path d="M12 6v6l4 2" stroke-width="2" stroke-linecap="round" />
            </svg>
            <span class="pill_title">{{ $t('action_set_press_time') }}</span>
          </div>
          <span class="pill_value">{{ pressTime }}<span>ms</span></span>
        </div>
        <div class="setting_slider_container">
          <input v-model.number="pressTime" class="slider_input" type="range" max="1000" min="150" step="10" :style="{ backgroundSize: timeBackgroundSize }" />
          <div class="slider_labels">
            <span>150ms</span>
            <span>1000ms</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 按键选择弹窗 -->
    <action_key_dialog v-model:visible="showKeyDialog" :row-index="selected" :base-tab="tab" :selected-keys="selected == MTTriggerType.click ? clickTriggerValue : pressTriggerValue" @confirm="onKeyConfirm" />
  </div>
</template>

<script lang="ts" setup>
import { actionSet } from '@/service/driver/action_set'
import { SetType, BaseTabs } from '@/service/driver/action_types'
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import trigger_btn from './trigger_btn.vue'
import action_key_dialog from './action_key_dialog.vue'
import { MagnetSeniorType } from '@libs/types/enum'
import { normalizeKeyCode, adjustFontSize } from '@/tool/utility'
import { operate } from '@/service/driver/operate'
import { currentSelectedKeyText, getVkText } from './action_config'
import { VK } from '@/define/define'
import { delay } from '@libs/utils/tools'

enum MTTriggerType {
  press = 0,
  click = 1,
}

const props = defineProps<{ tab: BaseTabs; keys: VK[] }>()

const showKeyDialog = ref(false)
let selected = ref(MTTriggerType.click)

let clickTriggerValue = ref<VK[]>([0, 0, 0])
let pressTriggerValue = ref<VK[]>([0, 0, 0])
let pressTime = ref(500)

const currentKey = computed(() => currentSelectedKeyText.value)

const clickSelectedKey = computed(() => {
  const code = clickTriggerValue.value?.[2] || 0
  return getVkText(code)
})

const holdSelectedKey = computed(() => {
  const code = pressTriggerValue.value?.[2] || 0
  return getVkText(code)
})

const hasClickKey = computed(() => clickTriggerValue.value && clickTriggerValue.value.some((c) => c > 0))
const hasHoldKey = computed(() => pressTriggerValue.value && pressTriggerValue.value.some((c) => c > 0))

onMounted(() => {
  initData()
  watchData()
})

watch(
  () => operate.currentKey.value,
  async () => {
    await delay(100) // 延迟更新数据，确保数据更新完成
    initData()
  },
)

const openKeyDialog = (t: MTTriggerType) => {
  selected.value = t
  const currentKeys = t == MTTriggerType.click ? clickTriggerValue.value : pressTriggerValue.value
  actionSet.initBaseKeysBySwitchInput(currentKeys)
  showKeyDialog.value = true
}

const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  if (selected.value == MTTriggerType.click) {
    clickTriggerValue.value = keys
  } else {
    pressTriggerValue.value = keys
  }
  showKeyDialog.value = false
}

const initData = () => {
  if (actionSet.magnetValue.KeyType == MagnetSeniorType.SeniorKeyTypeMT) {
    const keys = actionSet.magnetValue.Keys
    if (keys[1]) {
      clickTriggerValue.value = JSON.parse(JSON.stringify(keys[1].KeyCode))
    }
    if (keys[0]) {
      pressTriggerValue.value = JSON.parse(JSON.stringify(keys[0].KeyCode))
    }
    // 使用统一管理的模式时间
    pressTime.value = actionSet.modeTimes[SetType.mt]
  } else {
    actionSet.magnetValue.KeyType = MagnetSeniorType.SeniorKeyTypeMT
    actionSet.magnetValue.Keys = [
      { KeyCode: [0, 0, 0], KeyFunction: [], Travel: 0 },
      { KeyCode: [0, 0, 0], KeyFunction: [], Travel: 0 },
      { KeyCode: [0, 0, 0], KeyFunction: [], Travel: 0 },
      { KeyCode: [0, 0, 0], KeyFunction: [], Travel: 0 },
    ]
    clickTriggerValue.value = [0, 0, 0]
    pressTriggerValue.value = [0, 0, 0]
    pressTime.value = 500
    actionSet.modeTimes[SetType.mt] = 500
    actionSet.magnetValue.MTDuration = 50
  }
}

const watchData = () => {
  watch(
    [clickTriggerValue, pressTriggerValue, pressTime],
    () => {
      // 强制确保 KeyType 为 MT (2)，并补全 4 个 Keys 槽位
      actionSet.magnetValue.KeyType = MagnetSeniorType.SeniorKeyTypeMT

      const newKeys = [
        {
          KeyCode: pressTriggerValue.value,
          KeyFunction: [],
          Travel: 0,
        },
        {
          KeyCode: clickTriggerValue.value,
          KeyFunction: [],
          Travel: 0,
        },
        {
          KeyCode: [0, 0, 0],
          KeyFunction: [],
          Travel: 0,
        },
        {
          KeyCode: [0, 0, 0],
          KeyFunction: [],
          Travel: 0,
        },
      ]

      if (JSON.stringify(newKeys) !== JSON.stringify(actionSet.magnetValue.Keys)) {
        actionSet.magnetValue.Keys = newKeys
      }

      // 同步到统一管理变量
      const timeVal = Number(pressTime.value)
      if (actionSet.modeTimes[SetType.mt] !== timeVal) {
        actionSet.modeTimes[SetType.mt] = timeVal
      }

      const newDuration = Math.floor(timeVal / 10)
      if (actionSet.magnetValue.MTDuration !== newDuration) {
        actionSet.magnetValue.MTDuration = newDuration
      }
    },
    { deep: true, immediate: true },
  )
}

watch(
  () => actionSet.magnetValue.KeyType,
  (value) => {
    if (value == MagnetSeniorType.SeniorKeyTypeNone) {
      clickTriggerValue.value = [0, 0, 0]
      pressTriggerValue.value = [0, 0, 0]
      pressTime.value = 150
    }
  },
)

const timeBackgroundSize = computed(() => {
  const minTime = 150
  const maxTime = 1000
  const percent = ((pressTime.value - minTime) / (maxTime - minTime)) * 100
  return `${percent}%, 100%`
})
</script>

<style lang="less" scoped>
.action_mt_box {
  margin: auto;
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;

  .mt_mapping_board {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px);
    border-radius: var(--radius-l);
    padding: 16px 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;

    .board_inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 30px;
    }

    .key_slot {
      width: 130px;
      height: 70px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 10px 14px;
      border-radius: var(--radius-l);
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(0, 0, 0, 0.03);
      transition: var(--transition-base);

      .slot_label {
        font-size: 11px;
        color: var(--color-text-hint);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 2px;
      }

      .key_display {
        font-size: 20px;
        font-weight: 900;
        color: var(--color-dark);
        line-height: 1.2;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          color: var(--brand-color-pure);
        }
      }

      &.target {
        cursor: pointer;
        position: relative;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        padding: 0;
        height: 70px;

        &:hover {
          border-color: var(--brand-color-pure);
          box-shadow: var(--action-btn-shadow-hover);
          transform: translateY(-3px);
        }
      }
    }

    .mapping_branch {
      width: 60px;
      height: 80px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      .branch_line {
        position: absolute;
        left: 0;
        width: 100%;
        height: 1.5px;
        background: var(--color-gray-1);
        &.upper {
          top: 25%;
          transform: skewY(15deg);
        }
        &.lower {
          bottom: 25%;
          transform: skewY(-15deg);
        }
      }

      .branch_center {
        z-index: 2;
        background: var(--brand-color-pure);
        color: #fff;
        font-size: 9px;
        font-weight: 900;
        padding: 4px 8px;
        border-radius: var(--radius-m);
        box-shadow: 0 4px 10px rgba(255, 124, 0, 0.2);
      }
    }

    .target_column {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
  }

  .mt_settings_section {
    width: 100%;
  }
}
</style>
